import { FirebaseStorage } from '@capacitor-firebase/storage';
import { Capacitor } from '@capacitor/core';

// Conditionally import Filesystem only on native platforms
let Filesystem: any = null;
let Directory: any = null;

if (Capacitor.isNativePlatform()) {
  try {
    const filesystemModule = require('@capacitor/filesystem');
    Filesystem = filesystemModule.Filesystem;
    Directory = filesystemModule.Directory;
  } catch (e) {
    console.warn('@capacitor/filesystem not available, file uploads may not work on native platforms');
  }
}

// StorageReference-like object for compatibility
export interface StorageReference {
  fullPath: string;
  name: string;
  bucket: string;
}

// Create a StorageReference-like object
export function ref(storage: any, path: string): StorageReference {
  const pathParts = path.split('/');
  return {
    fullPath: path,
    name: pathParts[pathParts.length - 1],
    bucket: '', // Will be filled from metadata if needed
  };
}

// Upload string (base64 data URL) to Firebase Storage
export async function uploadString(
  storageRef: StorageReference,
  data: string,
  format: 'data_url' | 'base64' | 'base64url' | 'raw_string'
): Promise<{ ref: StorageReference }> {
  if (Capacitor.isNativePlatform()) {
    if (!Filesystem || !Directory) {
      throw new Error('@capacitor/filesystem is required for file uploads on native platforms. Please install it: npm install @capacitor/filesystem');
    }

    // For native platforms, convert data URL to file URI
    const base64Data = data.includes(',') ? data.split(',')[1] : data;
    const mimeType = data.includes('data:') ? data.split(';')[0].split(':')[1] : 'image/png';

    const extension = mimeType.split('/')[1] || 'png';
    const fileName = `temp_upload_${Date.now()}.${extension}`;

    // Save to filesystem first
    const { uri } = await Filesystem.writeFile({
      path: fileName,
      data: base64Data,
      directory: Directory.Cache,
    });

    // Upload the file
    return new Promise((resolve, reject) => {
      FirebaseStorage.uploadFile(
        {
          path: storageRef.fullPath,
          uri: uri,
        },
        (event, error) => {
          if (error) {
            reject(error);
          } else if (event?.completed) {
            resolve({ ref: storageRef });
          }
        }
      );
    });
  } else {
    // For web platform, convert data URL to Blob
    const base64Data = data.includes(',') ? data.split(',')[1] : data;
    const mimeType = data.includes('data:') ? data.split(';')[0].split(':')[1] : 'image/png';

    // Convert base64 to Blob
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    // Upload using blob
    return new Promise((resolve, reject) => {
      FirebaseStorage.uploadFile(
        {
          path: storageRef.fullPath,
          blob: blob,
        },
        (event, error) => {
          if (error) {
            reject(error);
          } else if (event?.completed) {
            resolve({ ref: storageRef });
          }
        }
      );
    });
  }
}

// Get download URL
export async function getDownloadURL(storageRef: StorageReference): Promise<string> {
  const result = await FirebaseStorage.getDownloadUrl({
    path: storageRef.fullPath,
  });
  return result.downloadUrl;
}

// Upload file (for web platform with Blob)
export async function uploadBytes(storageRef: StorageReference, data: Blob | Uint8Array | ArrayBuffer | ArrayBufferView): Promise<{ ref: StorageReference }> {
  if (Capacitor.isNativePlatform()) {
    throw new Error('uploadBytes is only supported on web. Use uploadString or uploadFile for native platforms.');
  }

  // For web, convert blob to data URL and use uploadString
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const result = await uploadString(storageRef, reader.result as string, 'data_url');
        resolve(result);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = reject;

    if (data instanceof Blob) {
      reader.readAsDataURL(data);
    } else if (data instanceof ArrayBuffer) {
      const blob = new Blob([new Uint8Array(data) as any]);
      reader.readAsDataURL(blob);
    } else if (ArrayBuffer.isView(data)) {
      const view = data as ArrayBufferView;
      const uint8 = view instanceof Uint8Array ? view : new Uint8Array((view as any).buffer as ArrayBuffer, (view as any).byteOffset || 0, (view as any).byteLength || 0);
      const blob = new Blob([uint8 as any]);
      reader.readAsDataURL(blob);
    } else {
      // Fallback: coerce to Blob
      const blob = new Blob([data as any]);
      reader.readAsDataURL(blob);
    }
  });
}

// Delete file
export async function deleteObject(storageRef: StorageReference): Promise<void> {
  await FirebaseStorage.deleteFile({
    path: storageRef.fullPath,
  });
}

// Get metadata
export async function getMetadata(storageRef: StorageReference) {
  const result = await FirebaseStorage.getMetadata({
    path: storageRef.fullPath,
  });
  return result;
}

// Update metadata
export async function updateMetadata(
  storageRef: StorageReference,
  metadata: {
    contentType?: string;
    customMetadata?: { [key: string]: string };
    cacheControl?: string;
    contentDisposition?: string;
    contentEncoding?: string;
    contentLanguage?: string;
  }
): Promise<void> {
  await FirebaseStorage.updateMetadata({
    path: storageRef.fullPath,
    metadata: {
      contentType: metadata.contentType,
      customMetadata: metadata.customMetadata,
      cacheControl: metadata.cacheControl,
      contentDisposition: metadata.contentDisposition,
      contentEncoding: metadata.contentEncoding,
      contentLanguage: metadata.contentLanguage,
    },
  });
}

// List files
export async function listAll(storageRef: StorageReference) {
  const result = await FirebaseStorage.listFiles({
    path: storageRef.fullPath,
  });
  return {
    items: result.items.map((item) => ({
      fullPath: item.path,
      name: item.name,
      bucket: item.bucket,
    })),
    prefixes: [], // Capacitor plugin doesn't support prefixes yet
  };
}

// Export a storage-like object for compatibility
export const storage = {
  // Placeholder object for compatibility
  // Actual operations use the functions above
};
